/**
 * Consultation SessionStorage Utilities
 * Manages persistence of consultation data across browser sessions
 */

export interface ConsultationData {
  beneficiaryId: string;
  currentStep: number;
  beneficiaryInfo: {
    firstname: string;
    lastname: string;
    birthDate: string;
    gender: string;
    diploma: string;
    postalCode: string;
    city: string;
  };
  beneficiarySituation: {
    situationType: string;
    otherSituation: string;
  };
  consultantInfo: {
    firstname: string;
    lastname: string;
  };
  consultationType: string;
  contextualData: string;
  selectedQuery: {
    queryType: string;
    jobPosition: string;
  };
  createdAt: string;
  lastModified: string;
}

const STORAGE_KEY = 'consultation_data';

/**
 * Save consultation data to sessionStorage
 */
export const saveConsultationData = (data: Partial<ConsultationData>): boolean => {
  try {
    const existingData = getConsultationData();
    const updatedData = {
      ...(existingData || {}),
      ...data,
      lastModified: new Date().toISOString(),
    } as ConsultationData;

    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(updatedData));
    return true;
  } catch (error) {
    console.error('Error saving consultation data:', error);
    return false;
  }
};

/**
 * Get consultation data from sessionStorage
 */
export const getConsultationData = (): ConsultationData | null => {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error('Error reading consultation data:', error);
  }

  return null;
};

/**
 * Initialize consultation data for a new beneficiary
 */
export const initializeConsultationData = (
  beneficiaryId: string,
  beneficiaryInfo: ConsultationData['beneficiaryInfo'],
  consultantInfo: any
): ConsultationData => {
  const data: ConsultationData = {
    beneficiaryId,
    currentStep: 1,
    beneficiaryInfo,
    beneficiarySituation: {
      situationType: '',
      otherSituation: '',
    },
    consultantInfo: {
      firstname: consultantInfo.firstname,
      lastname: consultantInfo.lastname,
    },
    consultationType: '',
    contextualData: '',
    selectedQuery: {
      queryType: '',
      jobPosition: '',
    },
    createdAt: new Date().toISOString(),
    lastModified: new Date().toISOString(),
  } as ConsultationData;

  saveConsultationData(data);
  return data;
};

/**
 * Clear consultation data from sessionStorage
 */
export const clearConsultationData = (): boolean => {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
    return true;
  } catch (error) {
    console.error('Error clearing consultation data:', error);
    return false;
  }
};

/**
 * Update specific step data
 */
export const updateStepData = (step: number, stepData: any): boolean => {
  const updateMap: { [key: number]: Partial<ConsultationData> } = {
    1: { currentStep: step },
    2: { beneficiaryInfo: stepData, currentStep: step },
    3: { beneficiarySituation: stepData, currentStep: step },
    4: { consultationType: stepData, currentStep: step },
    5: { contextualData: stepData, currentStep: step },
    6: { selectedQuery: stepData, currentStep: step },
  };

  const updates = updateMap[step];
  if (updates) {
    return saveConsultationData(updates);
  }

  return false;
};

/**
 * Validate consultation data completeness
 */
export const validateConsultationData = (data: ConsultationData): { [key: string]: boolean } => {
  if (!data || !data.beneficiaryId) {
    return {
      step1: false,
      step2: false,
      step3: false,
      step4: false,
      step5: false,
      step6: false,
    };
  }
  return {
    step1: true, // No validation needed for step 1
    step2: !!(
      data.beneficiaryInfo.birthDate &&
      data.beneficiaryInfo.gender &&
      data.beneficiaryInfo.diploma &&
      data.beneficiaryInfo.postalCode
    ),
    step3: !!(
      data.beneficiarySituation?.situationType &&
      (data.beneficiarySituation?.situationType !== 'autres' ||
        data.beneficiarySituation?.otherSituation)
    ),
    step4: !!data.consultationType,
    step5: !!(
      typeof data.contextualData === 'string' &&
      data.contextualData.trim() &&
      data.contextualData.trim().length >= 6
    ),
    step6: !!data.selectedQuery?.queryType,
  };
};

/**
 * Check if consultation is ready for submission
 */
export const isConsultationComplete = (data: ConsultationData): boolean => {
  try {
    const validation = validateConsultationData(data);
    return Object.values(validation).every(Boolean);
  } catch (error) {
    console.error('Error validating consultation data:', error);
    initializeConsultationData(data.beneficiaryId, data.beneficiaryInfo, data.consultantInfo);
    return false;
  }
};
